/**
 * Tableau — la table de données du cockpit, dans l'habit des captures
 * MoneyMaker : verre sombre très arrondi, hairlines, une lueur discrète aux
 * couleurs de la marque (`--color-mark`, cf `tableau.css`).
 *
 * Ce qu'il fait, et pourquoi :
 *   - Une page TIENT dans son cadre, le corps ne défile jamais. Le nombre de
 *     lignes par page est MESURÉ (`useLignesQuiTiennent`, sur `useContainerSize`
 *     de core), pas choisi — Kayla, 05-10-2026 : « l'intérêt d'une pagination,
 *     c'est de ne pas scroller vers le bas, il n'y a rien de pire sur une
 *     application ».
 *   - Des cases à cocher (`Checkbox` du cockpit), « tout cocher » sur la page,
 *     et une barre d'actions qui prend la place du titre quand des lignes sont
 *     cochées (le slot `actions`).
 *   - Le tri au clic sur l'en-tête (croissant → décroissant → rien ; un chiffre
 *     — colonne alignée à droite, ou `descDabord` — part du plus grand :
 *     décroissant → croissant → rien) : interne par défaut (`triInitial` pour
 *     en partir) ; contrôlé si `onTri` est donné, et c'est alors le parent qui
 *     trie `lignes` lui-même. L'en-tête triable est un `<button>` : il se trie
 *     aussi au clavier.
 *   - La page : interne par défaut ; contrôlée si `onPage` est donné (le parent
 *     la garde, elle survit à un démontage — une fiche ouverte, puis le retour).
 *     Un autre nombre de lignes ou un autre tri ramène en page 1.
 *   - UN bouton « Filtre », qui ouvre un panneau flottant dont le contenu est
 *     le slot `filtre` ; `filtresActifs` s'affiche sur le bouton.
 *   - Une ligne de totaux (`totaux`, par clé de colonne), calée en bas du corps,
 *     juste au-dessus des pages : c'est le `<tfoot>` de `ComptesTable` et des
 *     Offres RishFans.
 *   - Sous 640 px de large (`seuilEtroit`), les colonnes `secondaire`
 *     s'effacent, ou seules les `essentiel` restent (`largeurEtroite` par
 *     colonne) : c'est la largeur du TABLEAU qui décide (mesurée), pas celle de
 *     l'écran. Entre l'étroit et le large, une troisième taille : une colonne
 *     `desDe` n'apparaît qu'à partir de N px de tableau (la Créatrice de
 *     Comptes, qui n'a sa place que sur un grand panneau, jamais au prix du @).
 *     🪤 Le corps ne défile pas : un écran dont les colonnes à largeur fixe
 *     dépassent 640 px relève `seuilEtroit`, sinon la dernière colonne est
 *     coupée et celle sans largeur tombe à zéro.
 *   - Aucune donnée ne devient inaccessible (vague 2, 02-11-2026) : dès qu'une
 *     colonne est cachée, chaque ligne a un bouton « Détail » au bout, qui ouvre
 *     la FICHE de la ligne dans un tiroir (`Drawer` : la feuille du bas sur un
 *     téléphone) — toutes les colonnes, titre et valeur, par le même `rendu`.
 *     Pas un dépli dans le corps : une page tient dans son cadre et ne défile
 *     pas, un dépli la ferait déborder.
 *   - La ligne a un NOM (`nom`, sinon le texte de sa première colonne) : c'est
 *     lui que dit sa case à cocher (« Cocher Trista », jamais l'UUID) et que
 *     porte le titre de sa fiche.
 *   - Ce qu'il écrit de lui-même (« Tout cocher », « sélectionnés », « Filtre »,
 *     « Total », « Rien à afficher », « Oui / Non », le pied) vient des
 *     libellés de la librairie (`<Libelles>`, `libelles.tsx`) : traduisibles
 *     d'un coup ; `libelleTotaux` et `vide` l'emportent s'ils sont donnés.
 *   - Une rangée se clique (`onLigne`), se double-clique (`onLigneDouble` : la
 *     fiche) et s'ouvre au clic droit (`onLigneMenu` : un menu contextuel ;
 *     l'événement est à l'appelant, qui fait le `preventDefault`).
 *
 * Ce qui existait déjà sur `main`, et ce qu'on en fait : `Th`/`TablePagination`
 * de `panels/creators/orders/tableBits.tsx` (un `<th>` nu, un pied Préc./Suiv.
 * traduit par i18n) ne sont pas repris — pas de tri, pas de pilules, et i18n
 * empêcherait le port Helios ; le `DataTable` TanStack de `panels/sites` non
 * plus (une autre mécanique pour quelques centaines de lignes sans pagination
 * calée). Le hook de RishFans, lui, est repris en forme (cf
 * `useLignesQuiTiennent.ts`).
 *
 * Piloté par props, zéro Convex, zéro i18n, aucune API propre à React 19 : il
 * se porte tel quel dans Helios (React 18) et dans l'espace RishFans. Les
 * cellules « entité » et les pastilles vivent à côté (`CelluleEntite`,
 * `PastilleStatut`).
 *
 * Le pied est la `Pagination` de la librairie (lot 2 du catalogue) : ses
 * numéros de page sont des pilules de navigation, pas des actions — `Button`
 * garde les actions (Filtre, ‹ ›, Tout décocher).
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { cn } from "../utils";
import { ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown, Filter, X } from "../icons";
import { Button } from "../Button";
import { Checkbox } from "../Checkbox";
import { Drawer, DrawerBody } from "../Drawer";
import { useLibelles } from "../libelles";
import { Ligne, Lignes } from "../Lignes";
import { Pagination } from "../Pagination";
import { formatNombre } from "./formats";
import { colonnesVisibles, suivantTri, trierLignes, valeurDe, type Tri, type ValeurTriable } from "./pagination";
import { useLignesQuiTiennent } from "./useLignesQuiTiennent";
import "./tableau.css";

export interface Colonne<T> {
  /** Identifiant de la colonne ; sans `valeur`, c'est aussi la propriété lue sur la ligne. */
  cle: string;
  titre: ReactNode;
  /** L'explication au survol de l'en-tête (le `aide` de `ComptesTable`). */
  aide?: string;
  /** Le contenu de la cellule. Absent : la valeur, formatée (nombre fr-FR, date courte). */
  rendu?: (ligne: T) => ReactNode;
  /** Ce que le tri compare (et ce qu'on affiche sans `rendu`). Absent : `ligne[cle]`. */
  valeur?: (ligne: T) => ValeurTriable;
  aligne?: "gauche" | "droite" | "centre";
  triable?: boolean;
  /** Le premier clic trie du plus grand au plus petit. Défaut : oui pour une colonne alignée à droite (un chiffre),
   *  non pour un texte. */
  descDabord?: boolean;
  /** Largeur CSS (px ou chaîne). Les colonnes sans largeur se partagent le reste. */
  largeur?: number | string;
  /** Largeur en étroit (sous `seuilEtroit`) ; sinon `largeur`. */
  largeurEtroite?: number | string;
  /** S'efface quand le tableau est étroit (moins de `seuilEtroit` px de large). */
  secondaire?: boolean;
  /** Ne s'affiche qu'à partir de cette largeur du tableau (px) : la troisième taille, entre l'étroit et le large —
   *  la colonne Créatrice de Comptes n'a sa place que sur un grand panneau, jamais au prix du @. */
  desDe?: number;
  /** En étroit, seules les colonnes `essentiel` restent (si une colonne au moins l'est) :
   *  dans une colonne de 390 px, trois colonnes tiennent, pas six. */
  essentiel?: boolean;
}

export interface TableauProps<T> {
  colonnes: Colonne<T>[];
  lignes: T[];
  /** La clé stable d'une ligne (sélection, React). */
  cle: (ligne: T) => string;
  /** Le NOM d'une ligne, lisible : sa case à cocher (« Cocher Trista ») et le titre de sa fiche.
   *  Absent : le texte de la première colonne (sa `valeur`, sinon sa propriété), et la clé si ce n'est pas un texte. */
  nom?: (ligne: T) => string;
  titre?: ReactNode;
  sousTitre?: ReactNode;
  /** Les deux ensemble rendent le tableau cochable. */
  selection?: Set<string>;
  onSelection?: (selection: Set<string>) => void;
  /** Le tri de départ, quand le tri est interne. */
  triInitial?: Tri | null;
  /** Tri contrôlé : avec `onTri`, le parent trie `lignes` lui-même. Il reçoit le
   *  tri suivant du cycle (croissant → décroissant → rien) ET la colonne cliquée,
   *  pour appliquer son propre cycle s'il en a un. */
  tri?: Tri | null;
  onTri?: (tri: Tri | null, cle: string) => void;
  /** Le contenu du panneau « Filtre » ; absent, pas de bouton. */
  filtre?: ReactNode;
  filtresActifs?: number;
  /** La barre qui remplace le titre quand des lignes sont cochées. */
  actions?: ReactNode;
  /** À droite de la barre, avant le bouton Filtre (une recherche, un bouton). */
  outils?: ReactNode;
  /** La ligne de totaux, par clé de colonne ; la première colonne porte `libelleTotaux` si elle n'a rien. */
  totaux?: Partial<Record<string, ReactNode>>;
  /** Défaut : « Total » des libellés de la librairie. */
  libelleTotaux?: ReactNode;
  /** Défaut : « Rien à afficher » des libellés de la librairie. */
  vide?: string;
  /** Hauteur du cadre en px, ou `"parent"` (100 % du parent, qui doit avoir une hauteur). */
  hauteur?: number | "parent";
  /** Hauteur FIXE d'une ligne — c'est elle qui donne le nombre de lignes par page. */
  hauteurLigne?: number;
  /** Sous cette largeur du tableau (px), il passe en étroit. Défaut : 640. À
   *  relever au-dessus de la somme des `largeur` fixes, plus la place d'une
   *  colonne sans largeur : le corps ne défile pas, ce qui dépasse est coupé. */
  seuilEtroit?: number;
  /** Page contrôlée (à partir de 1) : avec `onPage`, le parent la garde. */
  page?: number;
  onPage?: (page: number) => void;
  /** Clic sur une rangée. */
  onLigne?: (ligne: T) => void;
  /** Double clic sur une rangée (ouvrir la fiche), clic droit (un menu contextuel — l'événement est à l'appelant). */
  onLigneDouble?: (ligne: T) => void;
  onLigneMenu?: (ligne: T, e: ReactMouseEvent<HTMLTableRowElement>) => void;
  className?: string;
}

function texteDe(v: ValeurTriable, oui: string, non: string): string {
  if (v === null || v === undefined || v === "") return "—";
  if (v instanceof Date) return v.toLocaleDateString("fr-FR");
  if (typeof v === "number") return formatNombre(v);
  if (typeof v === "boolean") return v ? oui : non;
  return String(v);
}

export function Tableau<T>({
  colonnes,
  lignes,
  cle,
  nom,
  titre,
  sousTitre,
  selection,
  onSelection,
  triInitial = null,
  tri: triControle,
  onTri,
  filtre,
  filtresActifs = 0,
  actions,
  outils,
  totaux,
  libelleTotaux,
  vide,
  hauteur = 560,
  hauteurLigne = 48,
  seuilEtroit = 640,
  page: pageControlee,
  onPage,
  onLigne,
  onLigneDouble,
  onLigneMenu,
  className,
}: TableauProps<T>) {
  const barre = useRef<HTMLDivElement>(null);
  const tete = useRef<HTMLTableSectionElement>(null);
  const piedTotaux = useRef<HTMLDivElement>(null);
  const pied = useRef<HTMLDivElement>(null);
  const reduit = useReducedMotion();
  const transition = reduit ? { duration: 0 } : { duration: 0.18, ease: "easeOut" as const };
  const libelles = useLibelles();
  const lt = libelles.tableau;
  const texte = (v: ValeurTriable) => texteDe(v, lt.oui, lt.non);
  /** Le nom lisible d'une ligne : `nom`, sinon sa première colonne, sinon sa clé. */
  const nomDe = (l: T): string => {
    if (nom) return nom(l);
    const premiere = colonnes[0];
    const t = premiere ? texte(valeurDe(l, premiere)) : "—";
    return t === "—" || t === "[object Object]" ? cle(l) : t;
  };

  // ── Le cadre : sa hauteur donne les lignes par page, sa largeur dit « étroit » ──
  const { ref: cadre, parPage: parPageMesure, largeur, hauteur: hauteurMesuree, pret } = useLignesQuiTiennent<HTMLDivElement>({
    hauteurLigne,
    reserve: () =>
      (barre.current?.offsetHeight ?? 0) +
      (tete.current?.offsetHeight ?? 0) +
      (piedTotaux.current?.offsetHeight ?? 0) +
      (pied.current?.offsetHeight ?? 0) +
      2,
  });
  // Une largeur à 0 (tests, onglet caché) n'est pas « étroit » : toutes les colonnes.
  const largeurMesuree = pret ? largeur : 0;
  const etroit = largeurMesuree > 0 && largeurMesuree < seuilEtroit;
  const visibles = useMemo(
    () => colonnesVisibles(colonnes, largeurMesuree, seuilEtroit),
    [colonnes, largeurMesuree, seuilEtroit],
  );
  // Des colonnes cachées (étroit, ou `desDe` pas atteint) : chaque ligne a sa fiche, rien n'est perdu.
  const avecDetail = visibles.length < colonnes.length;
  const [detail, setDetail] = useState<T | null>(null);

  // ── Tri ──
  const [triInterne, setTriInterne] = useState<Tri | null>(triInitial);
  const controle = onTri !== undefined;
  const tri = controle ? (triControle ?? null) : triInterne;
  const changerTri = (c: string) => {
    const col = colonnes.find((x) => x.cle === c);
    const suivant = suivantTri(tri, c, col?.descDabord ?? col?.aligne === "droite");
    if (controle) onTri?.(suivant, c);
    else setTriInterne(suivant);
  };
  const triees = useMemo(() => (controle ? lignes : trierLignes(lignes, tri, colonnes)), [controle, lignes, tri, colonnes]);

  // ── Pagination calée sur la hauteur ──
  const total = triees.length;
  // Un cadre sans hauteur (tests, onglet caché) ne pagine pas : tout, d'un coup.
  const parPage = pret && hauteurMesuree === 0 ? Math.max(1, total) : parPageMesure;
  const pages = Math.max(1, Math.ceil(total / parPage));
  const [pageInterne, setPageInterne] = useState(1);
  const pageAuParent = onPage !== undefined;
  const pageVoulue = pageAuParent ? (pageControlee ?? 1) : pageInterne;
  const setPage = (p: number) => (onPage ? onPage(p) : setPageInterne(p));
  const page = Math.min(pageVoulue, pages);
  // Un autre nombre de lignes (un filtre) ou un autre tri : retour en page 1.
  // Pas au montage — une page contrôlée revient telle quelle d'un démontage.
  const cleTri = tri ? `${tri.cle}:${tri.sens}` : "";
  const vu = useRef({ total, cleTri });
  useEffect(() => {
    if (vu.current.total === total && vu.current.cleTri === cleTri) return;
    vu.current = { total, cleTri };
    if (onPage) onPage(1);
    else setPageInterne(1);
    // `onPage` change à chaque rendu du parent : seuls le compte et le tri déclenchent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, cleTri]);
  const debut = (page - 1) * parPage;
  const pageLignes = pret ? triees.slice(debut, debut + parPage) : [];

  // ── Sélection ──
  const selectionnable = selection !== undefined && onSelection !== undefined;
  const clesPage = pageLignes.map(cle);
  const toutesCochees = selectionnable && clesPage.length > 0 && clesPage.every((k) => selection.has(k));
  const basculer = (k: string) => {
    if (!selectionnable) return;
    const s = new Set(selection);
    if (s.has(k)) s.delete(k);
    else s.add(k);
    onSelection(s);
  };
  const cocherPage = (oui: boolean) => {
    if (!selectionnable) return;
    const s = new Set(selection);
    for (const k of clesPage) {
      if (oui) s.add(k);
      else s.delete(k);
    }
    onSelection(s);
  };
  const nSel = selection?.size ?? 0;
  // Sans titre, outils, filtre ni sélection, pas de barre : l'écran a la sienne.
  const avecBarre = titre !== undefined || sousTitre !== undefined || outils !== undefined || filtre !== undefined || selectionnable;

  const style = {
    height: hauteur === "parent" ? "100%" : hauteur,
    "--tb-ligne": `${hauteurLigne}px`,
  } as CSSProperties;

  // Le même `<colgroup>` pour la table et pour la ligne de totaux (deux tables
  // en `table-layout: fixed`, même largeur → mêmes colonnes au pixel).
  const colgroup = (
    <colgroup>
      {selectionnable && <col style={{ width: etroit ? 38 : 44 }} />}
      {visibles.map((c) => {
        const l = etroit ? (c.largeurEtroite ?? c.largeur) : c.largeur;
        return <col key={c.cle} style={l !== undefined ? { width: l } : undefined} />;
      })}
      {avecDetail && <col style={{ width: 40 }} />}
    </colgroup>
  );
  const classeCellule = (c: Colonne<T>) =>
    cn("tb-td", c.aligne === "droite" && "tb-num", c.aligne === "centre" && "tb-centre");

  return (
    <div ref={cadre} className={cn("tb", className)} style={style}>
      {/* ── La barre : le titre, ou la sélection quand il y en a une ── */}
      {avecBarre && (
      <div ref={barre} className="tb-barre">
        <div className="tb-barre__gauche">
          <AnimatePresence mode="wait" initial={false}>
            {nSel > 0 ? (
              <motion.div
                key="selection"
                className="tb-selection"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={transition}
              >
                <span className="tb-selection__n tb-affichage">{formatNombre(nSel)}</span>
                <span className="tb-selection__l">{lt.selectionnes(nSel)}</span>
                {actions && <div className="tb-selection__actions">{actions}</div>}
                <Button variant="ghost" size="sm" icon={<X className="h-3 w-3" />} onClick={() => onSelection?.(new Set())}>
                  {lt.toutDecocher}
                </Button>
              </motion.div>
            ) : (
              <motion.div
                key="titre"
                className="min-w-0"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={transition}
              >
                {titre !== undefined && <div className="tb-titre tb-affichage">{titre}</div>}
                {sousTitre !== undefined && <div className="tb-sous">{sousTitre}</div>}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="tb-barre__droite">
          {outils}
          {filtre !== undefined && (
            <FiltreBouton contenu={filtre} actifs={filtresActifs} transition={transition} libelle={lt.filtre} nom={lt.filtres} />
          )}
        </div>
      </div>
      )}

      {/* ── Le corps : la table, jamais de défilement ── */}
      <div className="tb-corps">
        <table className="tb-table">
          {colgroup}
          <thead ref={tete}>
            <tr>
              {selectionnable && (
                <th className="tb-th tb-th--case">
                  <Checkbox checked={toutesCochees} onChange={cocherPage} size="sm" ariaLabel={lt.toutCocher} />
                </th>
              )}
              {visibles.map((c) => {
                const actif = tri?.cle === c.cle;
                const texte = (
                  <span className="tb-th__texte">
                    <span>{c.titre}</span>
                    {c.triable && (
                      <span className="tb-th__tri" aria-hidden>
                        {actif ? (
                          tri.sens === "asc" ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )
                        ) : (
                          <ChevronsUpDown className="h-3 w-3" />
                        )}
                      </span>
                    )}
                  </span>
                );
                return (
                  <th
                    key={c.cle}
                    title={c.aide}
                    className={cn(
                      "tb-th tb-etiquette",
                      c.triable && "is-triable",
                      actif && "is-tri",
                      c.aligne === "droite" && "tb-num",
                      c.aligne === "centre" && "tb-centre",
                    )}
                    aria-sort={actif ? (tri.sens === "asc" ? "ascending" : "descending") : undefined}
                  >
                    {/* Un vrai bouton : le tri se fait aussi au clavier (Tab, Entrée). */}
                    {c.triable ? (
                      <button type="button" className="tb-th__bouton" onClick={() => changerTri(c.cle)}>
                        {texte}
                      </button>
                    ) : (
                      texte
                    )}
                  </th>
                );
              })}
              {avecDetail && (
                <th className="tb-th tb-th--detail">
                  <span className="sr-only">{lt.detail}</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {pageLignes.map((l, i) => {
              const k = cle(l);
              const coche = selectionnable && selection.has(k);
              return (
                <motion.tr
                  key={`${page}:${k}`}
                  className={cn("tb-tr", coche && "is-coche", onLigne && "is-cliquable")}
                  initial={reduit ? false : { opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...transition, delay: reduit ? 0 : Math.min(i, 14) * 0.014 }}
                  onClick={onLigne ? () => onLigne(l) : undefined}
                  onDoubleClick={onLigneDouble ? () => onLigneDouble(l) : undefined}
                  onContextMenu={onLigneMenu ? (e) => onLigneMenu(l, e) : undefined}
                >
                  {selectionnable && (
                    <td className="tb-td tb-td--case" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={coche} onChange={() => basculer(k)} size="sm" ariaLabel={lt.cocher(nomDe(l))} />
                    </td>
                  )}
                  {visibles.map((c) => (
                    <td key={c.cle} className={classeCellule(c)}>
                      {c.rendu ? c.rendu(l) : texte(valeurDe(l, c))}
                    </td>
                  ))}
                  {avecDetail && (
                    <td className="tb-td tb-td--detail" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        icon={<ChevronRight className="h-3.5 w-3.5" />}
                        aria-label={`${lt.detail} ${nomDe(l)}`}
                        onClick={() => setDetail(l)}
                      />
                    </td>
                  )}
                </motion.tr>
              );
            })}
          </tbody>
        </table>
        {pret && total === 0 && <div className="tb-vide">{vide ?? lt.vide}</div>}

        {/* ── Les totaux : une seconde table, mêmes colonnes, calée en bas ── */}
        {totaux !== undefined && total > 0 && (
          <div ref={piedTotaux} className="tb-totaux">
            <table className="tb-table">
              {colgroup}
              <tbody>
                <tr>
                  {selectionnable && <td className="tb-td tb-td--case" />}
                  {visibles.map((c, i) => (
                    <td
                      key={c.cle}
                      className={cn(classeCellule(c), i === 0 && totaux[c.cle] === undefined && "tb-etiquette")}
                    >
                      {totaux[c.cle] ?? (i === 0 ? (libelleTotaux ?? lt.total) : null)}
                    </td>
                  ))}
                  {avecDetail && <td className="tb-td tb-td--detail" />}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Le pied : « 1–12 sur 205 » et les pages — la Pagination de la librairie ── */}
      <div ref={pied} className="tb-pied">
        <Pagination page={page} pages={pages} onPage={setPage} total={total} parPage={parPage} />
      </div>

      {/* ── La fiche d'une ligne, quand des colonnes sont cachées : tout, titre et valeur ── */}
      <Drawer
        isOpen={detail !== null}
        onClose={() => setDetail(null)}
        size="sm"
        title={detail !== null ? nomDe(detail) : lt.detail}
        closeLabel={libelles.fermer}
      >
        {detail !== null && (
          <DrawerBody>
            <Lignes ariaLabel={lt.detail}>
              {colonnes.map((c) => (
                <Ligne key={c.cle} etiquette={c.titre} valeur={c.rendu ? c.rendu(detail) : texte(valeurDe(detail, c))} />
              ))}
            </Lignes>
          </DrawerBody>
        )}
      </Drawer>
    </div>
  );
}

/** UN bouton Filtre, qui ouvre un panneau flottant ; se ferme au clic dehors et à Échap. */
function FiltreBouton({
  contenu,
  actifs,
  transition,
  libelle,
  nom,
}: {
  contenu: ReactNode;
  actifs: number;
  transition: { duration: number; ease?: "easeOut" };
  libelle: string;
  nom: string;
}) {
  const [ouvert, setOuvert] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ouvert) return;
    const clic = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOuvert(false);
    };
    const touche = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOuvert(false);
    };
    document.addEventListener("mousedown", clic);
    document.addEventListener("keydown", touche);
    return () => {
      document.removeEventListener("mousedown", clic);
      document.removeEventListener("keydown", touche);
    };
  }, [ouvert]);
  return (
    <div className="tb-filtre" ref={ref}>
      <Button
        variant={actifs > 0 ? "accent" : "secondary"}
        size="md"
        icon={<Filter className="h-3.5 w-3.5" />}
        onClick={() => setOuvert((o) => !o)}
        aria-expanded={ouvert}
      >
        {libelle}
        {actifs > 0 ? ` · ${actifs}` : ""}
      </Button>
      <AnimatePresence>
        {ouvert && (
          <motion.div
            className="tb-filtre__panneau"
            role="dialog"
            aria-label={nom}
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={transition}
          >
            {contenu}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
